import { useState } from "react";
import { PROOF_KIND_LABELS, proofDiff, proofOf, proofreadOn } from "@speisekarte/core";
import type { ProofEdit } from "@speisekarte/core";
import { api, messageOf, store } from "../store.ts";
import type { Data } from "../store.ts";

/**
 * „Deutsch prüfen“ (S47) in the app: only with it switched on (Konto → Sprachen), or with a language
 * (S61 § 11), does anything of it show (S38). The corrections stand where the text is edited, each on its own: „Übernehmen“ writes it
 * into the field (saved as any change), „Nein“ keeps it from coming back.
 */
export const proofOn = (data: Pick<Data, "settings">) => proofreadOn(data.settings);

/** A correction still open at a spot, with where it stands. */
export interface OpenEdit {
  key: string;
  where: string;
  edit: ProofEdit;
}

/** The corrections open in the texts as they are now (in a form: as typed). */
export function openEdits(data: Pick<Data, "settings" | "proofs">, spots: readonly { key: string; text: string; where: string }[]): OpenEdit[] {
  if (!proofOn(data)) return [];
  return spots.flatMap((s) => proofOf(s.text, data.proofs?.[s.key]).edits.map((edit) => ({ key: s.key, where: s.where, edit })));
}

/** Whether a text is still as checked, or changed only by corrections taken. */
export const stillChecked = (data: Pick<Data, "proofs">, key: string, text: string) => proofOf(text, data.proofs?.[key]).checked;

/** A space made visible where it is what changes. */
const shown = (s: string) => s.replace(/ /g, "␣");

/** The box of corrections at a form, a list, a menu. */
export function ProofBox({ edits, take, canEdit, where = true }: { edits: readonly OpenEdit[]; take: (e: OpenEdit) => void; canEdit: boolean; where?: boolean }) {
  const [error, setError] = useState<string | null>(null);
  if (edits.length === 0) return null;
  async function no(e: OpenEdit) {
    setError(null);
    try {
      await store.change(() => api("/api/proofread/reject", { body: { key: e.key, edit: e.edit.id } }));
    } catch (err) {
      setError(messageOf(err));
    }
  }
  return (
    <div className="proof-box" data-testid="proof-box">
      <p className="proof-head">
        <b>{edits.length === 1 ? "Ein Vorschlag" : `${edits.length} Vorschläge`}</b> <span className="tag soft">Deutsch prüfen</span>
        {canEdit && edits.length > 1 && (
          <button type="button" className="link" onClick={() => edits.forEach(take)} data-testid="proof-take-all">
            Alle übernehmen
          </button>
        )}
      </p>
      <ul>
        {edits.map((e) => {
          const d = proofDiff(e.edit.from, e.edit.to);
          return (
            <li key={`${e.key}/${e.edit.id}`} data-testid="proof-edit">
              <span className="proof-text">
                {where && <span className="muted small">{e.where}: </span>}
                {d.before}
                <del>{shown(d.out)}</del>
                <ins>{shown(d.in)}</ins>
                {d.after}
                <span className="muted small"> · {PROOF_KIND_LABELS[e.edit.kind]}</span>
              </span>
              {canEdit && (
                <span className="proof-acts">
                  <button type="button" onClick={() => take(e)} data-testid="proof-take">
                    Übernehmen
                  </button>
                  <button type="button" className="link" onClick={() => void no(e)} data-testid="proof-reject">
                    Nein
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className="error-text small">{error}</p>}
    </div>
  );
}
